import React, { useEffect, useState } from 'react';
import { withThumbnailSize } from '../../utils/imageUtils.js';
import { logImageFailure } from '../../utils/imageDiagnostics.js';

/**
 * `<img>` wrapper that prefers the Cloud CDN thumbnail variant when the
 * surrounding API payload includes `portrait_cdn_thumbnail_urls` (or
 * similar), and silently falls back to the proxy `?size=N` URL on a
 * cold-CDN 404 — which warms the GCS thumbnail for next render.
 *
 * Drop-in replacement for `<img src={withThumbnailSize(url, size)} ... />`.
 *
 * Props:
 *  - src: base image URL (proxy form, e.g. portrait_url)
 *  - size: 64 | 128 | 256 | 512
 *  - cdnThumbnailUrls: optional `{size: cdn_url}` map from the API
 *  - context: short tag forwarded to logImageFailure when both CDN and
 *    proxy variants fail; defaults to "ThumbnailImg"
 *  - onError: forwarded only when the proxy-form fallback also fails
 *    (preserves existing per-component "show placeholder" behavior)
 *  - All other props pass through to the underlying <img>.
 */
export default function ThumbnailImg({
  src,
  size,
  cdnThumbnailUrls = null,
  context = 'ThumbnailImg',
  onError,
  ...rest
}) {
  const [useProxy, setUseProxy] = useState(false);

  // Reset the CDN/proxy choice whenever the upstream source changes so
  // a new image gets a fresh chance at the CDN before falling back.
  useEffect(() => {
    setUseProxy(false);
  }, [src, cdnThumbnailUrls, size]);

  const computedSrc = withThumbnailSize(
    src,
    size,
    useProxy ? null : cdnThumbnailUrls,
  );

  const handleError = (e) => {
    if (!useProxy && cdnThumbnailUrls?.[size]) {
      // Cold CDN variant; switch to proxy form so the lazy-fill writes
      // the thumbnail for next time. Don't surface to the developer
      // console — this is the expected first-render path for any
      // cold image+size pair.
      setUseProxy(true);
      return;
    }
    // Both CDN and proxy variants failed — actual broken image.
    // Surface to the dev console so silent regressions are visible
    // (BOU-778). Cached failures dedupe inside useAuthorizedMediaUrl,
    // so this won't spam.
    logImageFailure(computedSrc, rest.alt, context);
    onError?.(e);
  };

  return <img {...rest} src={computedSrc} onError={handleError} />;
}
